/**
 * PageContainer — the single sanctioned page body wrapper for WeaveIn pages.
 *
 * Owns the page gutter (Triveny's 20px/22px rhythm ≈ px-4 py-5 sm:px-6) so
 * pages never set their own outer padding, max-width, min-h-screen, or
 * viewport background again.
 *
 * Widths: "full" (default — content spans the shell), "narrow" (max-w-3xl,
 * for settings/forms), or "wide" (max-w-7xl centered — ONLY for portal
 * surfaces with no sidebar, e.g. the member portal).
 */
import React from 'react'
import { useEmbeddedChrome } from './EmbeddedChrome'

interface PageContainerProps {
  children: React.ReactNode
  /** "full" (default), "narrow" (max-w-3xl, forms/settings), "wide" (max-w-7xl, sidebar-less portals). */
  size?: 'full' | 'narrow' | 'wide'
  /** Remove padding for full-bleed bodies (tables, boards, maps). */
  flush?: boolean
  /** Whether the container owns vertical scrolling (default true). */
  scroll?: boolean
  className?: string
}

export function PageContainer({
  children,
  size = 'full',
  flush = false,
  scroll = true,
  className = '',
}: PageContainerProps) {
  const embedded = useEmbeddedChrome()
  const padding = flush ? '' : embedded ? 'px-4 py-4 sm:px-6' : 'px-4 py-5 sm:px-6'
  // Embedded mounts scroll in the host shell; standalone mounts own the region.
  const overflow = scroll && !embedded ? 'overflow-y-auto' : ''
  const surface = embedded ? '' : 'flex-1 bg-paper'
  return (
    <div className={`${surface} ${overflow} ${padding} ${className}`}>
      {size === 'narrow' ? (
        <div className="mx-auto w-full max-w-3xl space-y-4">{children}</div>
      ) : size === 'wide' ? (
        <div className="mx-auto w-full max-w-7xl space-y-4">{children}</div>
      ) : (
        <div className="space-y-4">{children}</div>
      )}
    </div>
  )
}
